/** @jsxImportSource @emotion/react */
import tw from 'twin.macro';
import { css } from '@emotion/react';
import { useState, useEffect, useRef } from 'react';
import { Info } from 'components/shared/Info';
import { customToast } from 'utils/toast.util';
import { useToggle } from 'react-use';
import Loader from 'components/loader/loader';
import { isEmpty } from 'lodash';
import { WebhookService } from 'services/webhooks.service';
import { WebhookModal, WebhookModalRef } from './WebhookModal';
import { WebhookViewModal, WebhookViewModalRef } from './WebhookViewModal';
import { ModalOptions } from 'utils/enum';
import { Switcher } from 'components/shared/Switcher';
import { ReactComponent as WebhookIcon } from 'assets/Icons/memory.svg';
import { ReactComponent as TestIcon } from 'assets/Icons/chat_paste_go.svg';
import { ReactComponent as EditIcon } from 'assets/Icons/EditMode.svg';
import { ReactComponent as DeleteIcon } from 'assets/Icons/bin.svg';
import { ReactComponent as SettingIcon } from 'assets/Icons/setting.svg';
import { ReactComponent as AddIcon } from 'assets/Icons/add.svg';
import { Hint } from 'components/shared/Hint';
import { HintDisallowed } from 'components/shared/HintDisallowed';
import { disabledCss, hoverDropShadow } from 'components/shared/twin.styles';
import { Webhook, WebhookTestResult } from 'utils/models/webhook.model';
import { sortWebhookEvents } from 'utils/webhook.util';
import '../Settings/Preferences-Setting.scss';

export const WebhooksTab = () => {
  const [isLoading, toggleLoading] = useToggle(true);
  const [updatingWebhookId, setUpdatingWebhookId] = useState<string | null>(null);
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [activeWebhookMap, setActiveWebhookMap] = useState<Record<string, Set<string>>>({});
  const [maxActivePerEventType, setMaxActivePerEventType] = useState<number>(3);
  const webhookModalRef = useRef<WebhookModalRef>(null);
  const webhookViewModalRef = useRef<WebhookViewModalRef>(null);

  const fetchWebhooks = async () => {
    try {
      toggleLoading(true);
      const { data, maxActivePerEventType: limit } = (await WebhookService.getWebhooks()) as any;
      setWebhooks(data);
      if (typeof limit === 'number') setMaxActivePerEventType(limit);
    } catch (err) {
      customToast.error('Failed to load webhooks');
    } finally {
      toggleLoading(false);
    }
  };

  useEffect(() => {
    fetchWebhooks();
  }, []);

  useEffect(() => {
    const map: Record<string, Set<string>> = {};
    webhooks.forEach((w) => {
      if (w.state === 'active') {
        w.events.forEach((e) => {
          if (!map[e]) map[e] = new Set();
          map[e].add(w.webhookId);
        });
      }
    });
    setActiveWebhookMap(map);
  }, [webhooks]);

  const getLimitWarning = (webhook: Webhook) => {
    if (webhook.state === 'active') return null;
    for (const event of webhook.events) {
      const activeCount = activeWebhookMap[event]?.size || 0;
      if (activeCount >= maxActivePerEventType) return `Limit reached for event: ${event}`;
    }
    return null;
  };

  const handleAddWebhook = async () => {
    const result = await webhookModalRef.current?.show();
    if (result?.option === ModalOptions.YES) {
      try {
        await WebhookService.createWebhook(result.data);
        customToast.success('Webhook created successfully');
        fetchWebhooks();
      } catch (err) {
        customToast.error('Failed to create webhook');
      }
    }
  };

  const handleEditWebhook = async (webhook: Webhook) => {
    const result = await webhookModalRef.current?.show(webhook);
    if (result?.option === ModalOptions.YES) {
      try {
        await WebhookService.updateWebhook(webhook.webhookId, result.data);
        customToast.success('Webhook updated successfully');
        fetchWebhooks();
      } catch (err) {
        customToast.error('Failed to update webhook');
      }
    }
  };

  const handleViewWebhook = async (webhook: Webhook) => {
    await webhookViewModalRef.current?.show(webhook);
  };

  const handleDeleteWebhook = async (webhook: Webhook) => {
    if (webhook.state === 'active') {
      customToast.error('Cannot delete an active webhook. Please deactivate it first.');
      return false;
    }

    if (window.confirm('Are you sure you want to delete this webhook?')) {
      try {
        await WebhookService.deleteWebhook(webhook.webhookId);
        customToast.success('Webhook deleted');
        fetchWebhooks();
        return true;
      } catch (err) {
        customToast.error('Failed to delete webhook');
        return false;
      }
    }
    return false;
  };

  const handleTestWebhook = async (id: string) => {
    const testPromise = WebhookService.testWebhook(id, 'transcription.completed');

    customToast.promise(testPromise, {
      loading: 'Sending test payload...',
      success: 'Test payload sent',
      error: 'Failed to send test payload',
    });

    try {
      const { data } = (await testPromise) as { data: WebhookTestResult };

      if (data.statusCode >= 200 && data.statusCode < 300) {
        customToast.success(`Success. Testing successful! Webhook activated.`);
        // On successful test, set state to inactive (tested but not yet enabled) if currently pending/failed
        const currentHook = webhooks.find(w => w.webhookId === id);
        if (currentHook && (currentHook.state === 'pending' || currentHook.state === 'failed')) {
          await WebhookService.updateWebhook(id, { state: 'active', enabled: true });
          fetchWebhooks();
        }
      } else {
        const errorMsg = data.errorMessage || 'Unknown error';
        customToast.error(`Test failed (${data.statusCode}): ${errorMsg}`);
      }
    } catch (err) {
      // Error handled by toast.promise
    }
  };

  const handleToggleWebhook = async (webhook: Webhook, enabled: boolean) => {
    try {
      const newState = enabled ? 'active' : 'inactive';
      setUpdatingWebhookId(webhook.webhookId);
      await WebhookService.updateWebhook(webhook.webhookId, {
        enabled,
        state: newState
      });
      customToast.success(`Webhook ${enabled ? 'activated' : 'deactivated'}`);
      fetchWebhooks();
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to update status';
      customToast.error(errorMsg);
    } finally {
      setUpdatingWebhookId(null);
    }
  };

  return (
    <div tw="p-7 ml-3">
      <div tw="flex justify-between items-center mt-6 mb-6">
        <div tw="flex items-center space-x-4 ml-4">
          <WebhookIcon />
          <div tw="font-bold text-4xl">Webhooks</div>
        </div>
        <button
          className="button btn-primary large"
          tw="flex items-center (max-width[unset] w-auto px-3.5)! md-down:(w-full justify-center)! bg-sonnant-purple-2 text-white hover:bg-sonnant-purple-1"
          onClick={handleAddWebhook}
        >
          <span tw="flex mr-2">
            <AddIcon tw="width[16px] height[16px] fill[white]" />
          </span>
          Add Webhook
        </button>
      </div>

      <p tw="text-14 text-sonnant-grey-5 mb-6 ml-16">
        Manage outbound event notifications for your external services.
      </p>

      {isLoading ? (
        <div tw="flex justify-center p-10">
          <Loader />
        </div>
      ) : (
        <div tw="ml-16 mr-7">
          {isEmpty(webhooks) ? (
            <div tw="bg-white rounded-lg shadow overflow-hidden p-8">
              <Info text="No webhooks configured yet. Add one to start receiving event notifications." />
            </div>
          ) : (
            <div tw="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-6">
              {webhooks.map((row) => (
                <div
                  key={row.webhookId}
                  tw="bg-white rounded-lg border border-sonnant-grey-light shadow-md hover:shadow-lg transition-all flex flex-col h-full"
                >
                  <div
                    tw="p-6 flex-1 cursor-pointer"
                    onClick={() => handleViewWebhook(row)}
                  >
                    <div tw="flex justify-between items-start mb-4">
                      <div tw="flex-1 min-w-0">
                        <div tw="flex items-center space-x-3 mb-2">
                          <h4 tw="text-16 font-semibold text-sonnant-purple-3 m-0 overflow-hidden text-overflow[ellipsis] whitespace-nowrap">{row.name}</h4>
                          <span css={[statusBadgeCss(row.state)]} tw="flex-shrink-0">
                            {row.state}
                          </span>
                        </div>
                        <div tw="font-mono text-14 text-sonnant-grey-5 overflow-hidden text-overflow[ellipsis] whitespace-nowrap" title={row.url}>
                          {row.url}
                        </div>
                      </div>
                    </div>
                    <div tw="flex flex-wrap gap-2">
                      {sortWebhookEvents(row.events || []).map((event) => (
                        <span
                          key={event}
                          tw="text-12 bg-sonnant-grey-1 text-sonnant-grey-6 px-2 py-0.5 rounded border border-sonnant-grey-2 uppercase font-medium"
                        >
                          {event}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div tw="px-6 py-4 bg-sonnant-grey-1 rounded-b-lg border-t border-sonnant-grey-light">
                    {row.state === 'pending' && (
                      <div tw="mb-2 text-11 text-[var(--color-grey-8)] font-semibold italic">
                        New webhook. Must be tested successfully before activating.
                      </div>
                    )}
                    {row.state === 'failed' && (
                      <div tw="mb-2 text-11 text-[var(--color-grey-8)] font-semibold italic">
                        Delivery failed. Must be tested successfully before re-enabling.
                      </div>
                    )}
                    {row.state === 'inactive' && (
                      <div tw="mb-2 text-11 text-[var(--color-grey-8)] font-semibold italic">
                        Test successful. Please activate the webhook to start it working.
                      </div>
                    )}
                    {/* Rendering limit warning inside map loop needs to be handled carefully with map index or key if using separate function, but here we can just compute inline or call helper */}
                    <div tw="flex justify-between items-center column-gap[10px]">
                      <div tw="flex items-center flex-wrap">
                        {getLimitWarning(row) && (
                          <div tw="w-full mb-1 text-10 text-[var(--color-red)] font-semibold italic">
                            {getLimitWarning(row)}
                          </div>
                        )}
                        <Switcher
                          checked={row.state === 'active'}
                          onCheckedChange={(val) => handleToggleWebhook(row, val)}
                          disabled={updatingWebhookId === row.webhookId || row.state === 'pending' || row.state === 'failed' || !!getLimitWarning(row)}
                          isSmall
                        />
                        <span tw="ml-3 text-13 font-medium text-sonnant-grey-6">
                          {row.state === 'active' ? 'Active' : 'Inactive'}
                        </span>
                      </div>

                      <div tw="flex items-center space-x-2">
                        <HintDisallowed
                          disabled={false}
                          hintEnabled="Edit Webhook"
                        >
                          <div
                            tw="flex cursor-pointer fill-[var(--color-grey-5)]"
                            css={[hoverDropShadow(0.1)]}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditWebhook(row);
                            }}
                          >
                            <SettingIcon />
                          </div>
                        </HintDisallowed>

                        <HintDisallowed
                          disabled={row.state !== 'pending' && row.state !== 'failed'}
                          hintEnabled="Send Test Payload"
                          hintDisabled="Testing only required for Pending or Failed webhooks"
                        >
                          <div
                            tw="flex cursor-pointer fill-[var(--color-orange)]"
                            css={[
                              hoverDropShadow(0.1),
                              (row.state !== 'pending' && row.state !== 'failed') && tw`opacity-30 cursor-not-allowed pointer-events-none`
                            ]}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTestWebhook(row.webhookId);
                            }}
                          >
                            <TestIcon />
                          </div>
                        </HintDisallowed>

                        <HintDisallowed
                          disabled={row.state === 'active'}
                          hintEnabled="Delete Webhook"
                          hintDisabled="Cannot delete an active webhook"
                        >
                          <div
                            tw="flex cursor-pointer fill-[var(--color-red)]"
                            css={[
                              hoverDropShadow(0.1),
                              row.state === 'active' && tw`opacity-30 cursor-not-allowed pointer-events-none`
                            ]}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteWebhook(row);
                            }}
                          >
                            <DeleteIcon />
                          </div>
                        </HintDisallowed>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <WebhookModal ref={webhookModalRef} />
      <WebhookViewModal ref={webhookViewModalRef} />
    </div>
  );
};

const statusBadgeCss = (state: string) => {
  const colors: Record<string, any> = {
    pending: tw`bg-[var(--color-orange)] text-white`,
    inactive: tw`bg-[var(--color-grey-4)] text-white`,
    active: tw`bg-[var(--color-green)] text-white`,
    failed: tw`bg-[var(--color-red)] text-white`,
  };
  return [
    tw`px-2 py-0.5 rounded text-11 font-bold uppercase tracking-wider`,
    colors[state] || colors.inactive,
  ];
};
